import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getFilteredSurveyResults,
  getSurveyAnswers,
  getSurveyResults,
} from './data';
import { generateResults } from './data/mappers';
import {
  ServerSurveyResults,
  SurveyAnswer,
  SurveyQuestion,
  SurveyResultsObject,
  UnitType,
} from './types';

export type StoreState = {
  answerSurvey: SurveyAnswer;
  surveyQuestions: SurveyQuestion[];
  surveyReviewers: UnitType[];
  surveyResults: SurveyResultsObject;
  surveyRawResults: ServerSurveyResults;
};

export const initialSurveyStoreState: StoreState = {
  answerSurvey: {} as SurveyAnswer,
  surveyQuestions: [],
  surveyReviewers: [],
  surveyResults: {} as SurveyResultsObject,
  surveyRawResults: {} as ServerSurveyResults,
};

export enum SurveyActionKeyPrefixes {
  GET_SURVEY_ANSWERS = 'scenes/TopbarSurvey/SurveyAnswers/GET',
  GET_SURVEY_RESULTS = 'scenes/TopbarSurvey/SurveyResults/GET',
  GET_FILTERED_SURVEY_RESULTS = 'scenes/TopbarSurvey/FilteredSurveyResults/GET',
}

type SurveyAnswersPayload = {
  answerSurvey: SurveyAnswer;
};

type SurveyResultsPayload = {
  surveyResults: ServerSurveyResults;
};

type SurveyResultsProps = {
  id: string;
  unitId: number;
};

export const fetchSurveyAnswers = createAsyncThunk<
  SurveyAnswersPayload,
  string
>(
  SurveyActionKeyPrefixes.GET_SURVEY_ANSWERS,
  async (id: string): Promise<SurveyAnswersPayload> => {
    return {
      answerSurvey: await getSurveyAnswers(id),
    };
  },
);

export const fetchSurveyResults = createAsyncThunk<
  SurveyResultsPayload,
  string
>(
  SurveyActionKeyPrefixes.GET_SURVEY_RESULTS,
  async (id: string): Promise<SurveyResultsPayload> => {
    return {
      surveyResults: await getSurveyResults(id),
    };
  },
);

export const fetchFilteredSurveyResults = createAsyncThunk<
  SurveyResultsPayload,
  SurveyResultsProps
>(
  SurveyActionKeyPrefixes.GET_FILTERED_SURVEY_RESULTS,
  async ({ id, unitId }): Promise<SurveyResultsPayload> => {
    return {
      surveyResults: await getFilteredSurveyResults(id, unitId),
    };
  },
);

export const surveySlice = createSlice({
  name: '~/scenes/TopbarSurvey',
  initialState: initialSurveyStoreState,
  reducers: {
    closeAnswerAndResultsDialog: (state: StoreState) => {
      state.answerSurvey = {} as SurveyAnswer;
      state.surveyQuestions = [];
      state.surveyReviewers = [];
      state.surveyResults = {} as SurveyResultsObject;
      state.surveyRawResults = {} as ServerSurveyResults;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSurveyAnswers.fulfilled, (state, action) => {
      state.answerSurvey = action.payload.answerSurvey;
      state.surveyQuestions = action.payload.answerSurvey.questions;
      state.surveyReviewers = action.payload.answerSurvey.reviewers;
    });

    builder.addCase(fetchSurveyResults.fulfilled, (state, action) => {
      state.surveyRawResults = action.payload.surveyResults;
      state.surveyResults = generateResults(action.payload.surveyResults);
    });

    builder.addCase(fetchFilteredSurveyResults.fulfilled, (state, action) => {
      state.surveyRawResults = action.payload.surveyResults;
      state.surveyResults = generateResults(action.payload.surveyResults);
    });
  },
});

export const { closeAnswerAndResultsDialog } = surveySlice.actions;
export const surveyReducer = surveySlice.reducer;
